import React, { FC } from 'react';

import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import GiftImg from '~/modules/prepaid/assets/gift.webp';
import {
  DEFAULT_DISCOUNT_VALUE,
  FREE_AMOUNT_OF_DAYS,
  PrepaidBenefits,
  PrepaidPlaces,
} from '~/modules/prepaid/constants';
import { useCountdownTimer } from '~/modules/prepaid/hooks/useCountdownTimer';
import { usePrepaid } from '~/modules/prepaid/hooks/usePrepaid';
import analytics from '~/services/analytics';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { Typographies } from '~/theme/typography';

import styles from './styles.module.scss';

const SidebarPromo: FC = () => {
  const profile = useAppSelector((state) => state.profile);

  const { t } = useTranslation('');

  const nonPrepaidBenefit =
    profile.backend_ab_tests['experiment-prepaid-cards'] === 'b_test'
      ? PrepaidBenefits.DISCOUNT_35
      : PrepaidBenefits.EXTRA_WEEK_TRIAL;

  const { handleOpenNonPrepaidModal } = usePrepaid(nonPrepaidBenefit);

  const handleCardClick = (): void => {
    analytics.trackEvent('prepaid promo - click', {
      promo_type: nonPrepaidBenefit,
      promo_place: PrepaidPlaces.SIDEBAR,
    });
    handleOpenNonPrepaidModal();
  };

  const {
    countdown: { days, hours, minutes, seconds },
  } = useCountdownTimer(
    profile.user_subscription.started_at,
    profile.user_product.trial_period,
  );

  return (
    <div className={styles.container} onClick={handleCardClick}>
      <Typography
        variant={Typographies.TITLE_MEDIUM}
        className={styles.title}
        component="div"
      >
        <img src={GiftImg} alt="" className={styles.image} />

        <span>
          {nonPrepaidBenefit === PrepaidBenefits.EXTRA_WEEK_TRIAL
            ? t('prepaid:trial_card.title', {
                amount_of_days: FREE_AMOUNT_OF_DAYS,
                defaultValue: 'Get an Extra {{amount_of_days}} Days For Free',
              })
            : t('prepaid:discount_card.title', {
                discount_percentage: DEFAULT_DISCOUNT_VALUE,
                defaultValue: 'Get Permanent {{discount_percentage}}% Off',
              })}
        </span>
      </Typography>

      <Typography
        variant={Typographies.BODY_SMALL}
        className={styles.countdown}
        component="div"
      >
        {days >= 1 ? (
          <>
            {days}{' '}
            {t('prepaid:countdown.text', {
              context: 'days',
              defaultValue: 'days left before this offer ends',
            })}
          </>
        ) : (
          <>
            <span className={styles.time}>
              {hours}:{minutes}:{seconds}
            </span>{' '}
            {t('prepaid:countdown.text', {
              context: 'time',
              defaultValue: 'time left before this offer ends',
            })}
          </>
        )}
      </Typography>
    </div>
  );
};

export default SidebarPromo;
